import React, { useState } from 'react';

import { useMutation, useQuery } from '@apollo/client/react';
import { c, msgid, ngettext } from 'ttag';

import { Alert, Button, Modal, ModalContent, ModalFooter, RadioGroup } from '@freelancelabs/components';

import RefusalNotice from '../../components/RefusalNotice';
import { ListSkeleton } from '../../components/Skeletons';
import { AssignablePartnerListsDocument, SetUserPartnerListDocument } from '../../graphql/__generated__/types';
import { partnerListEmptiness } from './onboarding/catalogue';
import { partnerListRefusalOf } from './partnerListRefusal';

interface Props {
    userId: string;
    /** La liste actuellement attribuée, pour la pré-sélectionner. Nulle si aucune. */
    currentListId: string | null;
    onClose: () => void;
    /** Appelé après un enregistrement réussi : la fiche relit sa liste. */
    onSaved: () => void;
}

/**
 * La valeur du choix « aucune liste ». Un radio ne se décoche pas, et retirer la liste
 * doit rester possible : « aucune » est donc une option qu'on choisit, jamais un état où
 * l'on tombe. Elle repart en `partnerListId: null`. Même lecture que le `PartnerListStep`
 * de l'onboarding, dont ce dialog reprend le sélecteur.
 */
const NO_LIST = '';

/**
 * Changer ou retirer la liste de partenaires d'une personne (PCG-357).
 *
 * Jusqu'ici la liste n'était posée qu'à l'onboarding et la fiche était en lecture seule.
 * Ce dialog reprend le sélecteur de l'onboarding (même requête `assignablePartnerLists`,
 * qui ne sert que les listes VIVANTES) et `setUserPartnerList` enregistre l'état voulu :
 * une liste au plus, l'ancienne remplacée, ou aucune.
 *
 * La liste pend de la PERSONNE : l'API exige `manage_users` sur chacun de ses comptes,
 * et le refus se lit après le clic (le front ne connaît pas les droits par compte).
 */
const EditPartnerListDialog = ({ userId, currentListId, onClose, onSaved }: Props) => {
    const [selected, setSelected] = useState<string>(currentListId ?? NO_LIST);

    const { data, loading, error, refetch } = useQuery(AssignablePartnerListsDocument);
    const lists = (data?.assignablePartnerLists?.lists ?? []).filter(Boolean);
    const emptiness = partnerListEmptiness(error, data?.assignablePartnerLists?.emptyReason);

    const [save, { loading: saving, error: saveError }] = useMutation(SetUserPartnerListDocument);

    const options = [
        {
            value: NO_LIST,
            label: c('Partner list').t`No list: the « Partners » module stays empty for this person`,
        },
        ...lists.map((list) => {
            const count = list.partnerCount ?? 0;
            const name = list.name ?? '';
            const size =
                count === 0
                    ? c('Partner list').t`no partner yet`
                    : ngettext(msgid`${count} partner`, `${count} partners`, count);
            return { value: list.id as string, label: `${name} · ${size}` };
        }),
    ];

    const submit = () => {
        save({ variables: { userId, partnerListId: selected === NO_LIST ? null : selected } })
            .then((result) => {
                if (result.data) {
                    onSaved();
                    onClose();
                }
            })
            .catch(() => {});
    };

    // Rien à enregistrer quand le choix n'a pas bougé : on désactive plutôt qu'envoyer un
    // appel qui ne change rien.
    const unchanged = selected === (currentListId ?? NO_LIST);

    return (
        <Modal open onClose={onClose} title={c('Partner list').t`Change the partner list`} size={620}>
            <ModalContent>
                <div className="flex flex-col gap-4">
                    <p className="text-xs text-text-600">
                        {c('Partner list')
                            .t`The list decides which partners the person may consult in the « Partners » module. It belongs to the person, not to one of their accounts.`}
                    </p>

                    {loading && !data && <ListSkeleton rows={3} withControl />}

                    {!loading && emptiness && (
                        <Alert type={emptiness.tone} title={emptiness.title}>
                            <p className="text-sm">{emptiness.guidance}</p>
                            {emptiness.detail && (
                                <p className="mt-2 break-words font-mono text-xs text-text-600">{emptiness.detail}</p>
                            )}
                            {error && (
                                <div className="mt-2">
                                    <Button category="weak" size="small" onClick={() => refetch()}>
                                        {c('Action').t`Retry`}
                                    </Button>
                                </div>
                            )}
                        </Alert>
                    )}

                    {/* « Aucune » reste offert même catalogue vide : c'est le choix que le
                        dialog sait enregistrer, et le cacher laisserait l'encart au-dessus
                        passer pour tout le contenu. */}
                    <RadioGroup name="partner-list" value={selected} onValue={setSelected} options={options} />

                    {saveError && <RefusalNotice refusal={partnerListRefusalOf(saveError)} />}
                </div>
            </ModalContent>
            <ModalFooter>
                <Button category="weak" onClick={onClose} disabled={saving}>
                    {c('Action').t`Cancel`}
                </Button>
                <Button category="default" onClick={submit} loading={saving} disabled={unchanged}>
                    {c('Action').t`Save`}
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default EditPartnerListDialog;
